import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@tickchak/react-share/live';
import {TEticketSeatPos} from '../../../../redux/eTicket/eTicket.type';
import BlockView from '../../../../assets/icons/BlockView';
import SeatView from '../../../../assets/icons/SeatView';
import useSeatZoom, {MAX_ZOOM, MIN_ZOOM} from './useSeatZoom';
import * as css from './SeatGrid.css';

const CELL = 12;
const STEP = CELL + 3;

interface SeatGridProps {
    seats: TEticketSeatPos[];
    mySeat?: {seat?: string | number; row?: string | number};
    onBack: () => void;
}

function SeatGrid({seats, mySeat, onBack}: SeatGridProps) {
    const {t} = useTranslation('eTicket');
    const viewportRef = useRef<HTMLDivElement>(null);
    const [viewport, setViewport] = useState<{width: number; height: number}>();

    // Match only the seat of the current ticket (by row + seat), not every seat in the order.
    const isMine = (seat: TEticketSeatPos) =>
        mySeat != null && String(seat.seat) === String(mySeat.seat) && String(seat.row) === String(mySeat.row);

    const layout = useMemo(() => {
        const xs = seats.map(s => s.x);
        const ys = seats.map(s => s.y);
        const originX = Math.min(...xs);
        const originY = Math.min(...ys);
        return {
            originX,
            originY,
            width: (Math.max(...xs) - originX + 1) * STEP,
            height: (Math.max(...ys) - originY + 1) * STEP,
            mine: seats.find(isMine)
        };
    }, [seats, mySeat]);

    useLayoutEffect(() => {
        const node = viewportRef.current;
        if(!node) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setViewport({width: entry.contentRect.width, height: entry.contentRect.height});
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // The grid is drawn at its natural size and scaled to fit here rather than by `max-width`,
    // so the anchor below can be expressed in the same on-screen px the transform works in.
    const fitScale = viewport
        ? Math.min(1, viewport.width / layout.width, viewport.height / layout.height)
        : 1;

    const anchor = useMemo(() => {
        if(!layout.mine) {
            return undefined;
        }
        return {
            x: ((layout.mine.x - layout.originX) * STEP + CELL / 2 - layout.width / 2) * fitScale,
            y: ((layout.mine.y - layout.originY) * STEP + CELL / 2 - layout.height / 2) * fitScale
        };
    }, [layout, fitScale]);

    const {zoom, setZoom, pan, reset, dragHandlers} = useSeatZoom(anchor);

    // Memoized so panning/zooming only restyles the canvas transform, not all the seat nodes.
    const grid = useMemo(() => (
        <svg
            width={layout.width}
            height={layout.height}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            role="img"
            aria-label={t('seatMapTitle')}
        >
            {seats.map((seat, index) => {
                const mine = isMine(seat);
                return (
                    <g key={index} transform={`translate(${(seat.x - layout.originX) * STEP} ${(seat.y - layout.originY) * STEP})`}>
                        <rect width={CELL} height={CELL} rx={2} className={mine ? 'is-mine' : undefined} />
                        {mine ? (
                            <text x={CELL / 2} y={CELL / 2} textAnchor="middle" dominantBaseline="central">{seat.seat}</text>
                        ) : (
                            <path className="seat-x" d="M3.5 3.5 L8.5 8.5 M3.5 8.5 L8.5 3.5" />
                        )}
                    </g>
                );
            })}
        </svg>
    ), [seats, layout, mySeat, t]);

    return (
        <css.wrap>
            <css.viewport ref={viewportRef} {...dragHandlers} data-cy="seat-grid">
                <css.canvas style={{transform: `translate(${pan.x}px, ${pan.y}px) scale(${fitScale * zoom})`}}>
                    {grid}
                </css.canvas>
            </css.viewport>
            <css.controls>
                <css.slider>
                    <css.iconBtn type="button" onClick={onBack} aria-label={t('seatMapBlockView')} data-cy="seat-grid-blocks">
                        <BlockView />
                    </css.iconBtn>
                    <input
                        type="range"
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        step={0.1}
                        value={zoom}
                        onChange={e => setZoom(Number(e.target.value))}
                        aria-label={t('seatMapZoom')}
                    />
                    <css.iconBtn type="button" onClick={() => setZoom(MAX_ZOOM)} aria-label={t('seatMapSeatView')} data-cy="seat-grid-seat-view">
                        <SeatView />
                    </css.iconBtn>
                </css.slider>
                <Button variant="primary" size="primary" onClick={reset} data-cy="seat-grid-reset">{t('reset')}</Button>
            </css.controls>
        </css.wrap>
    );
}

export default SeatGrid;
